import _ from 'lodash';
import PropTypes from 'prop-types';
import constants from '../../config/constants';
import { withApplicationContext } from '../../utils/ApplicationContext';
import { withCart } from '../../utils/CartContext';
import { withDigitalData } from '../../utils/DigitalDataContext';
import useDeliveryDataPersistence from '../../utils/hooks/useDeliveryDataPersistence';
import { withLabels } from '../../utils/LabelsContext';
import { withProduct } from '../../utils/ProductContext';
import { getVariant } from '../../utils/variant';
import MatchMedia from '../ui/MatchMedia/MatchMedia';
import { AvailabilityDesktop } from './AvailabilityDektop/AvailabilityDesktop';
import AvailabilityDesktopV2 from './AvailabilityDektop/AvailabilityDesktop/v2';
import { AvailabilityMobile } from './AvailabilityMobile/AvailabilityMobile';

const Availability = ({
  appCtx,
  labels,
  product,
  digitalData,
  cart,
  sizes,
  colorVariants,
  regionCode,
  store,
  isStoreAvailabilityVisibleInPDP,
  is1pSeller,
  selectedZone,
  zoneConfig,
  setZoneConfig,
  isDefaultComuna,
  badgeItems,
  comunaName,
}) => {
  const { deviceType, reloadOnZoneChange, siteConfig } = appCtx;
  const isModernLineContainerEnabled = !!appCtx?.siteConfig?.toggles?.isModernLineContainerEnabled;
  const {
    currentVariant: currentVariantId,
    variants,
    productType,
    showShippingModal,
    shippingEstimatedPrice,
    attributes,
    pickupInStoreData,
    stockCategoryConfig,
    stockCategoryLimit,
  } = product;

  const {
    isHDAvailable: isHomeDeliveryAvailable,
    isCCAvailable: isPickFromStoreAvailable,
    isOnlineSellable,
    isHDVisible,
    isCCVisible,
    isOnlineSellableVisible,
    offerings = [],
    isFreeShippingApplicable = false,
    meatStickers = [],
  } = getVariant(variants, currentVariantId);

  const offeringId = offerings.length > 0 && offerings[0].offeringId ? offerings[0].offeringId : currentVariantId;
  const sellerId = _.get(offerings[0], 'sellerId', '');

  const {
    deliveryData,
    deliveryActions,
    selectedType,
    setSelectedType,
    textGenerated,
    deliveryErrors,
    selectedDeliveryOption,
    setSelectedDeliveryOption,
  } = useDeliveryDataPersistence({
    store,
    selectedZone,
    regionCode,
    currentVariantId,
    labels,
  });

  digitalData.pdp.setHomeDelivery(isHomeDeliveryAvailable);
  digitalData.pdp.setStorePickup(isPickFromStoreAvailable);

  const getStateOfStock = ({ state = false, isStockInStore = false }) => {
    if (isStockInStore) {
      return !!store || is1pSeller ? constants.AVAILABILITY_INSTOCK : constants.AVAILABILITY_OUTOFSTOCK;
    }
    return state ? constants.AVAILABILITY_INSTOCK : constants.AVAILABILITY_OUTOFSTOCK;
  };

  const getProcessedAvailability = () => {
    const AvailabilityMap = {
      PurchaseOnline: {
        shippingOptionType: constants.AVAILABILITY_PURCHASEONLINE,
        stateOfStock: getStateOfStock({ state: isOnlineSellable }),
      },
      PickupInStore: {
        shippingOptionType: constants.AVAILABILITY_PICKUPINSTORE,
        stateOfStock: getStateOfStock({ state: isPickFromStoreAvailable }),
      },
      HomeDelivery: {
        shippingOptionType: constants.AVAILABILITY_HOMEDELIVERY,
        stateOfStock: getStateOfStock({ state: isHomeDeliveryAvailable }),
      },
      StockInStore: {
        shippingOptionType: constants.AVAILABILITY_STOCKINSTORE,
        stateOfStock: getStateOfStock({ isStockInStore: isStoreAvailabilityVisibleInPDP }),
      },
    };

    const processedAvailabilityList = [
      {
        isVisible: isStoreAvailabilityVisibleInPDP,
        data: AvailabilityMap.StockInStore,
      },
      {
        isVisible: isHDVisible,
        data: AvailabilityMap.HomeDelivery,
      },
      {
        isVisible: isCCVisible,
        data: AvailabilityMap.PickupInStore,
      },
      {
        isVisible: isOnlineSellableVisible,
        data: AvailabilityMap.PurchaseOnline,
      },
    ];

    const isPurchaseOnlineVisibleInPDP = 0;

    if (isPurchaseOnlineVisibleInPDP) {
      processedAvailabilityList.push(AvailabilityMap.PurchaseOnline);
    }

    return processedAvailabilityList
      .map(({ isVisible, data }) => {
        if (isVisible) {
          return data;
        }
        return undefined;
      })
      .filter(Boolean);
  };

  const processedAvailabilityList = getProcessedAvailability();

  if (processedAvailabilityList.length === 0) {
    return null;
  }
  const { isHDExpressEnabled = false } = siteConfig.toggles || {};

  const availabilityProps = {
    availabilityList: processedAvailabilityList,
    sizes,
    colorVariants,
    cart,
    deliveryData,
    deliveryActions,
    selectedType,
    setSelectedType,
    textGenerated,
    deliveryErrors,
    selectedDeliveryOption,
    setSelectedDeliveryOption,
    zoneConfig,
    setZoneConfig,
    badgeItems,
    comunaName,
    appCtxData: {
      store,
      selectedZone,
      isDefaultComuna,
      regionCode,
      deviceType,
      reloadOnZoneChange,
      endpoints: siteConfig.endpoints,
      isHDExpressEnabled,
    },
    labelsData: {
      FEW_UNITS_LEFT: 'Quedan pocas unidades',
      EXACT_UNITS_LEFT: 'Quedan solo {units} unidades',
      UNITS_AVAILABLE: 'Unidades disponibles',
      EXACT_UNITS_AVAILABLE: '{units} unidades disponibles',
      ...labels,
    },
    productData: {
      currentVariantId,
      variants,
      productType,
      showShippingModal,
      shippingEstimatedPrice,
      attributes,
      pickupInStoreData,
      stockCategoryConfig,
      stockCategoryLimit,
    },
    variantData: {
      isHomeDeliveryAvailable,
      isPickFromStoreAvailable,
      isOnlineSellable,
      isHDVisible,
      isCCVisible,
      isOnlineSellableVisible,
      offerings,
      isFreeShippingApplicable,
      meatStickers,
      offeringId,
      sellerId,
    },
  };

  const renderAvailabilityDesktop = () => {
    return isModernLineContainerEnabled ? (
      <AvailabilityDesktopV2 availabilityCommonProps={availabilityProps} />
    ) : (
      <AvailabilityDesktop availabilityCommonProps={availabilityProps} />
    );
  };

  return (
    <MatchMedia
      content={{
        mobile: () => <AvailabilityMobile availabilityCommonProps={availabilityProps} />,
        tablet: () => <AvailabilityMobile availabilityCommonProps={availabilityProps} />,
        desktop: () => renderAvailabilityDesktop(),
      }}
    />
  );
};
Availability.propTypes = {
  product: PropTypes.object.isRequired,
  digitalData: PropTypes.object.isRequired,
  cart: PropTypes.object.isRequired,
  regionCode: PropTypes.string.isRequired,
  store: PropTypes.string.isRequired,
  isStoreAvailabilityVisibleInPDP: PropTypes.bool.isRequired,
  is1pSeller: PropTypes.bool.isRequired,
  appCtx: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  sizes: PropTypes.array,
  colorVariants: PropTypes.array,
  selectedZone: PropTypes.object,
  zoneConfig: PropTypes.object,
  setZoneConfig: PropTypes.func,
  isDefaultComuna: PropTypes.bool,
  badgeItems: PropTypes.array,
  comunaName: PropTypes.string.isRequired,
};

Availability.defaultProps = {
  sizes: [],
  colorVariants: [],
  selectedZone: '',
  zoneConfig: {},
  setZoneConfig: () => {},
  isDefaultComuna: false,
  badgeItems: [],
};

export default withApplicationContext(withLabels(withProduct(withDigitalData(withCart(Availability)))));
